import React, { useState } from 'react';
import ArrayBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/ArrayBuilder';
import type { TypeBuilderMapType } from 'react-scale/corp/components/QM/taskTemplatesEditor/BuilderSchemasType';
import { Icon, Typography, SelectMenu } from '@scale/scaleui';
import { AutoQCRoutingThresholdTypeSchemaMap } from '@scale/shared/pipelinev3/genai';
import {
  NumericValueComparator,
  AutoQCThreshold,
  AutoQCThresholdType,
  NumericValueComparatorToNameMap,
} from '@scale/shared-types';

interface AutoQCRoutingThresholdsBuilderProps {
  name: string;
  existingValue?: Array<AutoQCThreshold>;
  updateProperty: (arg: Array<AutoQCThreshold> | undefined) => void;
  required?: boolean;
  typeBuilderMap: TypeBuilderMapType;
}

const autoQCThresholdOptions = [
  {
    id: AutoQCThresholdType.OverallScore,
    name: 'Overall Score',
  },
  {
    id: AutoQCThresholdType.TaxonomyScore,
    name: 'Taxonomy Score',
  },
  {
    id: AutoQCThresholdType.TaxonomyCriteriaScore,
    name: 'Taxonomy-Criteria Score',
  },
];

const getThresholdLabel = (threshold: AutoQCThreshold) => {
  if (threshold.type === AutoQCThresholdType.OverallScore) {
    return `Overall Score ${NumericValueComparatorToNameMap[threshold.comparator]} ${
      threshold.score
    }`;
  } else if (threshold.type === AutoQCThresholdType.TaxonomyScore) {
    if (!threshold.taxonomy) {
      return 'Incomplete Taxonomy Threshold';
    }
    return `${threshold.taxonomy} ${NumericValueComparatorToNameMap[threshold.comparator]} ${
      threshold.score
    }`;
  } else if (threshold.type === AutoQCThresholdType.TaxonomyCriteriaScore) {
    if (!threshold.taxonomy || !threshold.criteria) {
      return 'Incomplete Taxonomy-Criteria Threshold';
    }
    return `${threshold.taxonomy}-${threshold.criteria} ${
      NumericValueComparatorToNameMap[threshold.comparator]
    } ${threshold.score}`;
  }
  return 'Unsupported Threshold Type';
};

const AutoQCRoutingThresholdsBuilder: React.FC<AutoQCRoutingThresholdsBuilderProps> = ({
  name,
  existingValue = [],
  updateProperty,
  typeBuilderMap,
}) => {
  const thresholds = existingValue;
  const addThreshold = (type: AutoQCThresholdType) => {
    const newThresholds = [...thresholds];
    newThresholds.push({
      type,
      score: 0,
      comparator: NumericValueComparator.Equals,
      ...([AutoQCThresholdType.TaxonomyCriteriaScore, AutoQCThresholdType.TaxonomyScore].includes(
        type,
      ) && {
        taxonomy: '',
      }),
      ...(type === AutoQCThresholdType.TaxonomyCriteriaScore && {
        criteria: '',
      }),
    } as AutoQCThreshold);

    updateProperty(newThresholds);
  };

  const [selectedType, setSelectedType] = useState<AutoQCThresholdType>(
    AutoQCThresholdType.TaxonomyCriteriaScore,
  );

  return (
    <div className="scaleui scaleui-tailwind scale-theme">
      {name && <div className="my-2 text-sm font-medium leading-6 text-neutral-700">{name}</div>}
      <ArrayBuilder
        typeBuilderMap={typeBuilderMap}
        elements={thresholds}
        setElements={updateProperty}
        getKey={threshold => JSON.stringify(threshold)}
        getTitle={getThresholdLabel}
        getSchema={threshold => AutoQCRoutingThresholdTypeSchemaMap[threshold.type]}
        delimiter={<Typography.Subtitle4>AND</Typography.Subtitle4>}
      />
      <div className="flex">
        <SelectMenu
          className="min-w-[135px]"
          options={autoQCThresholdOptions}
          selectedOptionId={selectedType}
          setSelected={value => {
            setSelectedType(value as AutoQCThresholdType);
          }}
        />
        <Icon
          className="ml-4"
          hasBackground
          icon={['fas', 'plus']}
          onClick={() => addThreshold(selectedType)}
          size="lg"
          variant="primary"
        />
      </div>
    </div>
  );
};

export default AutoQCRoutingThresholdsBuilder;
